import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  MessageSquare,
  CheckCircle2,
  CircleDashed,
  Clock,
  ChevronDown,
  Facebook,
  Instagram,
  Phone,
  Music2,
  MessageCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import PageHeader from './ui/PageHeader';
import StatCard from './ui/StatCard';
import EmptyState from './ui/EmptyState';
import apiService from '../services/apiService';
import { useTheme } from '../contexts/ThemeContext';
import {
  TIME_RANGES,
  PLATFORM_META,
  INTENT_LABELS,
  SENTIMENT_LABELS,
  platformMeta,
  chartEntriesFromMap,
} from '../utils/conversationSummaries';

const PLATFORM_FILTERS = [
  { id: 'all', label: 'Tüm kanallar' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'voice', label: 'Telefon' },
];

const INTENT_COLORS = {
  randevu_talebi: '#4a9e8a',
  fiyat_sorgusu: '#38bdf8',
  bilgi_talebi: '#a78bfa',
  sikayet: '#f87171',
  diger: '#94a3b8',
  unknown: '#64748b',
};

function PlatformIcon({ platform, className = 'h-3.5 w-3.5' }) {
  const id = platformMeta(platform).id;
  if (id === 'instagram') return <Instagram className={className} />;
  if (id === 'tiktok') return <Music2 className={className} />;
  if (id === 'whatsapp') return <MessageCircle className={className} />;
  if (id === 'voice') return <Phone className={className} />;
  return <Facebook className={className} />;
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Dropdown({ value, options, onChange, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = options.find((o) => o.id === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <span ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-lg bg-white/[0.04] px-2.5 py-1 text-xs font-medium text-slate-300 ring-1 ring-white/10 transition-colors hover:bg-white/[0.08] hover:text-white"
      >
        {active.label}
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <span
          role="listbox"
          className="absolute left-0 top-full z-20 mt-1 flex min-w-[9rem] flex-col rounded-xl border border-white/10 bg-[#1a1d27] p-1 shadow-xl shadow-black/40"
        >
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              role="option"
              aria-selected={option.id === value}
              onClick={() => {
                onChange(option.id);
                setOpen(false);
              }}
              className={`rounded-lg px-3 py-1.5 text-left text-xs font-medium transition-colors ${
                option.id === value
                  ? 'bg-emerald-400/15 text-emerald-200'
                  : 'text-slate-300 hover:bg-white/[0.06] hover:text-white'
              }`}
            >
              {option.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

const emptyStats = {
  total: 0,
  resolved: 0,
  open: 0,
  highLeads: 0,
  avgLeadScore: null,
  byPlatform: {},
  byIntent: {},
  bySentiment: {},
  recent: [],
};

const Dashboard = ({ onOpenSummaries }) => {
  const { isLight } = useTheme();
  const chartColors = isLight
    ? {
        axis: '#64748b',
        axisLine: 'rgba(15,23,42,0.15)',
        grid: 'rgba(15,23,42,0.08)',
        cursor: 'rgba(15,23,42,0.05)',
        tooltipBg: '#ffffff',
        tooltipBorder: 'rgba(15,23,42,0.12)',
        tooltipText: '#1e293b',
        pieStroke: '#ffffff',
      }
    : {
        axis: '#94a3b8',
        axisLine: 'rgba(255,255,255,0.15)',
        grid: 'rgba(255,255,255,0.08)',
        cursor: 'rgba(255,255,255,0.04)',
        tooltipBg: '#1a1d27',
        tooltipBorder: 'rgba(255,255,255,0.09)',
        tooltipText: '#d4d8e2',
        pieStroke: 'rgba(5,7,10,0.6)',
      };

  const [rangeId, setRangeId] = useState('all');
  const [platformId, setPlatformId] = useState('all');
  const [stats, setStats] = useState(emptyStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await apiService.fetchSummaryStats({
        range: rangeId,
        platform: platformId,
      });
      setStats(data || emptyStats);
    } catch (err) {
      setError(err.message);
      setStats(emptyStats);
    } finally {
      setLoading(false);
    }
  }, [rangeId, platformId]);

  useEffect(() => {
    setLoading(true);
    load();
    return apiService.addUpdateListener((event) => {
      if (event.type === 'conversation_summary') load();
    });
  }, [load]);

  const intentChart = useMemo(
    () =>
      chartEntriesFromMap(
        stats.byIntent,
        (k) => INTENT_LABELS[k] || k,
        (k) => INTENT_COLORS[k] || '#4a9e8a'
      ),
    [stats.byIntent]
  );

  const channelChart = useMemo(
    () =>
      chartEntriesFromMap(
        stats.byPlatform,
        (k) => platformMeta(k).short,
        (k) => platformMeta(k).color
      ),
    [stats.byPlatform]
  );

  const sentimentChart = useMemo(
    () =>
      chartEntriesFromMap(
        stats.bySentiment,
        (k) => SENTIMENT_LABELS[k] || k,
        (k) =>
          ({ positive: '#34d399', neutral: '#94a3b8', negative: '#f87171', unknown: '#64748b' }[k] ||
          '#94a3b8')
      ),
    [stats.bySentiment]
  );

  const liveBadge = (
    <div className="flex items-center rounded-lg bg-emerald-400/10 px-3 py-2 ring-1 ring-emerald-400/20">
      <div className="mr-2 h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
      <span className="text-sm font-medium text-emerald-300">Canlı</span>
    </div>
  );

  const headerDescription = (
    <>
      <span>
        {`Özel Aktif International Hospital Kocaeli — ${format(new Date(), 'd MMMM yyyy', {
          locale: tr,
        })}`}
      </span>
      <Dropdown
        label="Zaman aralığı"
        value={rangeId}
        options={TIME_RANGES.map(({ id, label }) => ({ id, label }))}
        onChange={setRangeId}
      />
      <Dropdown
        label="Kanal"
        value={platformId}
        options={PLATFORM_FILTERS}
        onChange={setPlatformId}
      />
    </>
  );

  return (
    <div className="page-container">
      <PageHeader
        title="Sosyal Medya Özet Paneli"
        description={headerDescription}
        badge={liveBadge}
        actions={
          onOpenSummaries ? (
            <button type="button" onClick={onOpenSummaries} className="btn-secondary text-sm">
              Görüşme Özetleri
            </button>
          ) : null
        }
      />

      {error && (
        <p className="mb-6 rounded-lg bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</p>
      )}

      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        <StatCard title="Toplam görüşme" value={loading ? '…' : stats.total} icon={MessageSquare} accent="blue" isLive />
        <StatCard title="Tamamlandı" value={loading ? '…' : stats.resolved} icon={CheckCircle2} accent="green" isLive />
        <StatCard title="Devam Eden" value={loading ? '…' : stats.open} icon={CircleDashed} accent="orange" isLive />
        <StatCard
          title="Facebook"
          value={loading ? '…' : stats.byPlatform?.facebook || 0}
          icon={Facebook}
          accent="indigo"
          isLive
        />
        <StatCard
          title="Instagram"
          value={loading ? '…' : stats.byPlatform?.instagram || 0}
          icon={Instagram}
          accent="purple"
          isLive
        />
        <StatCard
          title="TikTok"
          value={loading ? '…' : stats.byPlatform?.tiktok || 0}
          icon={Music2}
          accent="blue"
          isLive
        />
        <StatCard
          title="WhatsApp"
          value={loading ? '…' : stats.byPlatform?.whatsapp || 0}
          icon={MessageCircle}
          accent="green"
          isLive
        />
        <StatCard
          title="Telefon"
          value={loading ? '…' : stats.byPlatform?.voice || 0}
          icon={Phone}
          accent="amber"
          isLive
        />
      </div>

      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="card overflow-hidden">
          <h3 className="mb-4 text-lg font-semibold text-white">Kanala göre</h3>
          {channelChart.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-400">Henüz kanal verisi yok</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <Pie
                  data={channelChart}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percent }) => `%${(percent * 100).toFixed(0)}`}
                  outerRadius={72}
                  dataKey="value"
                  stroke={chartColors.pieStroke}
                >
                  {channelChart.map((entry) => (
                    <Cell key={entry.key} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, _name, item) => [
                    value,
                    platformMeta(item?.payload?.key).label,
                  ]}
                  contentStyle={{
                    background: chartColors.tooltipBg,
                    border: `1px solid ${chartColors.tooltipBorder}`,
                    borderRadius: 6,
                    color: chartColors.tooltipText,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.keys(PLATFORM_META).map((id) => (
              <span
                key={id}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ring-1 ring-inset ${PLATFORM_META[id].className}`}
              >
                <PlatformIcon platform={id} />
                {PLATFORM_META[id].label}: {stats.byPlatform?.[id] || 0}
              </span>
            ))}
          </div>
        </div>

        <div className="card lg:col-span-2">
          <h3 className="mb-4 text-lg font-semibold text-white">Niyet dağılımı</h3>
          {intentChart.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-400">Henüz niyet verisi yok</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={intentChart}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: chartColors.axis }} stroke={chartColors.axisLine} />
                <YAxis allowDecimals={false} tick={{ fill: chartColors.axis }} stroke={chartColors.axisLine} />
                <Tooltip
                  cursor={{ fill: chartColors.cursor }}
                  contentStyle={{
                    background: chartColors.tooltipBg,
                    border: `1px solid ${chartColors.tooltipBorder}`,
                    borderRadius: 6,
                    color: chartColors.tooltipText,
                  }}
                />
                <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                  {intentChart.map((entry) => (
                    <Cell key={entry.key} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-white">Duygu</h3>
          {sentimentChart.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-400">Henüz duygu verisi yok</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <Pie
                  data={sentimentChart}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={70}
                  dataKey="value"
                  stroke={chartColors.pieStroke}
                  labelLine={false}
                  label={({ percent }) => `%${(percent * 100).toFixed(0)}`}
                >
                  {sentimentChart.map((entry) => (
                    <Cell key={entry.key} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, _name, item) => [
                    value,
                    SENTIMENT_LABELS[item?.payload?.key] || item?.payload?.name || '',
                  ]}
                  contentStyle={{
                    background: chartColors.tooltipBg,
                    border: `1px solid ${chartColors.tooltipBorder}`,
                    borderRadius: 6,
                    color: chartColors.tooltipText,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
          {sentimentChart.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {sentimentChart.map((entry) => (
                <span
                  key={entry.key}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] px-2 py-0.5 text-xs text-slate-300 ring-1 ring-white/10"
                >
                  <span className="h-2 w-2 rounded-full" style={{ background: entry.color }} />
                  {SENTIMENT_LABELS[entry.key] || entry.name}: {entry.value}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="card lg:col-span-2">
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Son görüşmeler</h3>
            <div className="flex items-center text-sm text-slate-300">
              <Clock className="mr-1 h-4 w-4" />
              {stats.recent?.length > 0
                ? `Son ${stats.recent.length} görüşme`
                : 'Henüz görüşme yok'}
            </div>
          </div>

          {!stats.recent?.length ? (
            <EmptyState
              icon={MessageSquare}
              title="Henüz görüşme özeti yok"
              description="Messenger (veya ileride Instagram / telefon) görüşmeleri sessiz kaldığında özetler burada ve Görüşme Özetleri’nde görünür."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Zaman
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Kanal
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Müşteri
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Niyet
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Lead
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Özet
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {stats.recent.map((row, index) => {
                    const meta = platformMeta(row.platform);
                    return (
                      <tr
                        key={`${row.platform}-${row.externalUserId}-${row.version}`}
                        className={`transition-colors hover:bg-white/5 ${index === 0 ? 'bg-emerald-400/[0.07]' : ''}`}
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-200">
                          {formatDateTime(row.windowEnd || row.createdAt)}
                          {index === 0 && (
                            <span className="ml-2 inline-flex items-center rounded bg-emerald-400/15 px-2 py-0.5 text-xs font-medium text-emerald-300 ring-1 ring-emerald-400/20">
                              En yeni
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${meta.className}`}
                          >
                            <PlatformIcon platform={row.platform} />
                            {meta.label}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-200">
                          {row.customerName || row.externalUserId}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-300">
                          {INTENT_LABELS[row.intent] || row.intent || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-300">
                          {row.leadScore != null ? row.leadScore : '—'}
                        </td>
                        <td className="max-w-xs truncate px-4 py-3 text-sm text-slate-300">{row.summary}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
